import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Crown } from "lucide-react";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(null);
  const [founder, setFounder] = useState({ founder_count: 0, spots_left: 100, is_open: true });

  useEffect(() => {
    api.get("/billing/tiers").then(({ data }) => setTiers(data.tiers || {})).catch(() => {});
    api.get("/founder-status").then(({ data }) => setFounder(data)).catch(() => {});
  }, []);

  const checkout = async (tier) => {
    if (!user) {
      navigate("/auth?mode=signup");
      return;
    }
    setLoading(tier);
    try {
      const { data } = await api.post("/billing/checkout", {
        tier,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setLoading(null);
    }
  };

  const order = ["CHIC", "DIVA", "ICON"];

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F5F5F5]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="text-center mb-14">
          <span className="font-label text-[0.65rem] text-[#D4AF37]">Memberships</span>
          <h1 className="font-display text-5xl sm:text-7xl font-normal mt-4 text-[#F5F5F5]" data-testid="pricing-title">
            Choose your <span className="italic text-[#D4AF37]">presence.</span>
          </h1>
          {founder.is_open && (
            <div className="mt-8 inline-flex items-center gap-3 px-6 py-3 goji-glass" data-testid="pricing-founder-badge">
              <Crown className="w-4 h-4 text-[#D4AF37]" />
              <span className="font-label text-[0.65rem] text-[#F5F5F5]/80">
                <span className="text-[#D4AF37]">{founder.spots_left}</span> places remain — FREE FOR LIFETIME — FOUNDER STATUS
              </span>
            </div>
          )}
          {user?.is_founder && (
            <p className="mt-6 text-[#D4AF37] font-display text-2xl italic" data-testid="pricing-founder-status">
              You are a Founder. ICON granted for life.
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[#D4AF37]/15">
          {order.map((key) => {
            const tier = tiers[key];
            if (!tier) return null;
            const isIcon = key === "ICON";
            const isPopular = key === "DIVA";
            return (
              <div
                key={key}
                className={`relative bg-[#1A1A1A] p-10 ${isIcon ? "icon-glow" : ""}`}
                data-testid={`pricing-tier-${key.toLowerCase()}`}
              >
                {isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#1A1A1A] border border-[#D4AF37] font-label text-[0.6rem] text-[#D4AF37]">
                    Most Chosen
                  </span>
                )}
                {isIcon && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#1A1A1A] border border-[#D4AF37] font-label text-[0.6rem] text-[#D4AF37] flex items-center gap-1.5">
                    <Crown className="w-3 h-3" /> Top Tier
                  </span>
                )}
                <h3 className="font-display text-4xl font-normal text-[#D4AF37]">{tier.name}</h3>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-xl text-[#F5F5F5]/60">€</span>
                  <span className="font-display text-6xl font-normal">{String(tier.price).replace(".", ",")}</span>
                  <span className="text-sm text-[#F5F5F5]/45 ml-1">/ month</span>
                </div>
                <ul className="mt-10 space-y-4">
                  {tier.perks?.map((p) => (
                    <li key={p} className="flex gap-3 text-sm text-[#F5F5F5]/80">
                      <span className="text-[#D4AF37] mt-1.5 w-2 h-px bg-[#D4AF37] flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => checkout(key)}
                  disabled={loading === key}
                  className="mt-10 w-full btn-ghost"
                  data-testid={`pricing-checkout-${key.toLowerCase()}`}
                >
                  {loading === key ? "Redirecting..." : `Choose ${tier.name}`}
                </button>
              </div>
            );
          })}
        </div>

        <p className="mt-14 text-center font-label text-[0.6rem] text-[#F5F5F5]/35">
          Test mode — Stripe sandbox. No real charges.
        </p>
      </div>

      <Footer />
    </div>
  );
}
